import { Injectable } from '@angular/core';

/**
 * Generates stable, restrained colours from document ids.  The chosen text colour
 * is always black or white, whichever provides the stronger WCAG contrast.
 */
@Injectable({ providedIn: 'root' })
export class MesocycleColorService {
  private readonly palette = [
    8, 22, 36, 50, 72, 96, 132, 158, 184, 204, 222, 246, 274, 296, 322, 342,
  ];

  colorFor(mesocycleId: string): string {
    if (!mesocycleId) throw new Error('A mesocycle id is required to generate a colour');
    const hue = this.palette[this.hash(mesocycleId) % this.palette.length];
    return `hsl(${hue} 58% 44%)`;
  }

  /** Returns an accessible foreground for a generated/persisted CSS colour. */
  textColorFor(background: string): '#000000' | '#ffffff' {
    const luminance = this.relativeLuminance(background);
    const blackContrast = (luminance + 0.05) / 0.05;
    const whiteContrast = 1.05 / (luminance + 0.05);
    return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
  }

  contrastRatio(foreground: string, background: string): number {
    const first = this.relativeLuminance(foreground);
    const second = this.relativeLuminance(background);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
  }

  private hash(value: string): number {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index++) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  private relativeLuminance(color: string): number {
    const rgb = this.toRgb(color);
    const channels = rgb.map((channel) => {
      const normalized = channel / 255;
      return normalized <= 0.04045
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  }

  private toRgb(color: string): [number, number, number] {
    const hex = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(color);
    if (hex) {
      const value = hex[1].length === 3
        ? hex[1].split('').map((part) => part + part).join('')
        : hex[1];
      return [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16)) as [number, number, number];
    }

    const hsl = /^hsl\(\s*([\d.]+)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%\s*\)$/i.exec(color);
    if (!hsl) throw new Error(`Unsupported colour format: ${color}`);
    const hue = ((Number(hsl[1]) % 360) + 360) % 360;
    const saturation = Number(hsl[2]) / 100;
    const lightness = Number(hsl[3]) / 100;
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const x = chroma * (1 - Math.abs((hue / 60) % 2 - 1));
    const match = lightness - chroma / 2;
    const [red, green, blue] = hue < 60 ? [chroma, x, 0] : hue < 120 ? [x, chroma, 0] : hue < 180 ? [0, chroma, x] : hue < 240 ? [0, x, chroma] : hue < 300 ? [x, 0, chroma] : [chroma, 0, x];
    return [red, green, blue].map((channel) => Math.round((channel + match) * 255)) as [number, number, number];
  }
}
